/**
 * Admin session operations
 */

import { collection, doc, addDoc, updateDoc, deleteDoc, getDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import { db } from '../../firebase'
import { logSessionDelete, logSessionEdit } from '../audit.service'
import {
  SESSIONS_COLLECTION,
  type ManualEntryParams,
  type SessionDocument,
  type SessionWithId
} from './types'

/** Break update data for editing */
export interface BreakUpdateData {
  id: string
  startTime: Date
  endTime: Date | null
}

/** Update session parameters */
export interface UpdateSessionParams {
  sessionId: string
  checkInTime?: string
  checkOutTime?: string
  dailyReport?: string
  flagReason?: string | null
  breaks?: BreakUpdateData[]
  adminId: string
  adminName: string
}

/**
 * Approve a session (admin only)
 */
export async function approveSession(sessionId: string, adminId: string): Promise<void> {
  await updateDoc(doc(db, SESSIONS_COLLECTION, sessionId), {
    approved: true,
    approvedAt: serverTimestamp(),
    approvedBy: adminId,
    updatedAt: serverTimestamp()
  })
}

/**
 * Create manual entry (admin only)
 */
export async function createManualEntry(params: ManualEntryParams): Promise<SessionWithId> {
  const { userId, userName, date, checkInTime, checkOutTime, reason, adminId } = params

  // Calculate totalWorkMinutes from times
  const [inHours, inMinutes] = checkInTime.split(':').map(Number)
  const [outHours, outMinutes] = checkOutTime.split(':').map(Number)
  const checkInTotalMinutes = inHours * 60 + inMinutes
  const checkOutTotalMinutes = outHours * 60 + outMinutes
  let totalWorkMinutes = checkOutTotalMinutes - checkInTotalMinutes
  if (totalWorkMinutes < 0) {
    totalWorkMinutes += 24 * 60 // Handle overnight shifts
  }

  // Get user's hourly rate to calculate gross pay
  let grossPay: number | null = null
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)
  if (userSnap.exists()) {
    const userData = userSnap.data()
    const ratePerHour = userData.ratePerHour || 0
    grossPay = Math.round((totalWorkMinutes / 60) * ratePerHour * 100) / 100
  }

  // Manual entries are always completed and approved since admin creates them
  const status: 'completed' = 'completed'
  const flagReason: string | null = null

  const sessionData: SessionDocument = {
    userId,
    userName,
    date,
    siteId: null,
    siteName: null,
    checkIn: {
      timestamp: serverTimestamp(),
      time: checkInTime,
      lat: null,
      lng: null,
      accuracy: null,
      address: 'Manual entry',
      photo1Url: null,
      photo2Url: null,
      isMockLocation: false,
      deviceInfo: 'Manual entry by admin'
    },
    breaks: [],
    checkOut: {
      timestamp: serverTimestamp(),
      time: checkOutTime,
      lat: null,
      lng: null,
      accuracy: null,
      address: 'Manual entry',
      photo1Url: null,
      photo2Url: null,
      isMockLocation: false,
      deviceInfo: 'Manual entry by admin'
    },
    dailyReport: 'Manual entry',
    totalWorkMinutes,
    totalBreakMinutes: 0,
    grossPay,
    status,
    flagReason,
    approved: true,
    approvedAt: serverTimestamp(),
    approvedBy: adminId,
    isManualEntry: true,
    manualEntryReason: reason,
    manualEntryBy: adminId,
    manualEntryAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }

  const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), sessionData)

  return {
    id: docRef.id,
    ...sessionData
  }
}

/**
 * Delete session parameters
 */
export interface DeleteSessionParams {
  sessionId: string
  adminId: string
  adminName: string
  reason?: string
}

/**
 * Delete a session (admin only)
 * This is a hard delete - the session is permanently removed
 */
export async function deleteSession(params: DeleteSessionParams): Promise<void> {
  const { sessionId, adminId, adminName, reason } = params

  // Get the session data before deleting (for audit log)
  const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId)
  const sessionSnap = await getDoc(sessionRef)

  if (!sessionSnap.exists()) {
    throw new Error('Session not found')
  }

  const sessionData = sessionSnap.data() as SessionDocument

  // Delete the session
  await deleteDoc(sessionRef)

  // Log the deletion
  await logSessionDelete(
    adminId,
    adminName,
    {
      id: sessionId,
      userName: sessionData.userName || 'Unknown',
      date: sessionData.date,
      totalWorkMinutes: sessionData.totalWorkMinutes
    },
    reason
  )
}

/**
 * Calculate total break minutes from breaks array
 */
function calculateTotalBreakMinutes(breaks: { startTime: Timestamp; endTime: Timestamp | null; duration: number | null }[]): number {
  return breaks.reduce((total, brk) => {
    if (brk.duration !== null) {
      return total + brk.duration
    }
    if (brk.startTime && brk.endTime) {
      const startDate = brk.startTime.toDate ? brk.startTime.toDate() : new Date(brk.startTime as unknown as string)
      const endDate = brk.endTime.toDate ? brk.endTime.toDate() : new Date(brk.endTime as unknown as string)
      const diffMs = endDate.getTime() - startDate.getTime()
      return total + Math.round(diffMs / 60000)
    }
    return total
  }, 0)
}

/**
 * Update session details (admin only)
 * Allows editing check-in time, check-out time, daily report, flag status, and breaks
 */
export async function updateSession(params: UpdateSessionParams): Promise<void> {
  const { sessionId, checkInTime, checkOutTime, dailyReport, flagReason, breaks, adminId, adminName } = params

  // Get the current session data for audit logging
  const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId)
  const sessionSnap = await getDoc(sessionRef)

  if (!sessionSnap.exists()) {
    throw new Error('Session not found')
  }

  const currentData = sessionSnap.data() as SessionDocument

  // Build update object with only changed fields
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    lastEditedBy: adminId,
    lastEditedAt: serverTimestamp()
  }

  const changes: Record<string, { from: unknown; to: unknown }> = {}

  // Update check-in time if provided
  if (checkInTime !== undefined && checkInTime !== currentData.checkIn?.time) {
    updates['checkIn.time'] = checkInTime
    changes.checkInTime = { from: currentData.checkIn?.time, to: checkInTime }
  }

  // Update check-out time if provided
  if (checkOutTime !== undefined && checkOutTime !== currentData.checkOut?.time) {
    updates['checkOut.time'] = checkOutTime
    changes.checkOutTime = { from: currentData.checkOut?.time, to: checkOutTime }
  }

  // Recalculate totalWorkMinutes and grossPay if times changed
  const finalCheckInTime = checkInTime ?? currentData.checkIn?.time
  const finalCheckOutTime = checkOutTime ?? currentData.checkOut?.time

  if ((checkInTime !== undefined || checkOutTime !== undefined) && finalCheckInTime && finalCheckOutTime) {
    // Parse times (format: "HH:MM")
    const [inHours, inMinutes] = finalCheckInTime.split(':').map(Number)
    const [outHours, outMinutes] = finalCheckOutTime.split(':').map(Number)

    // Calculate total minutes
    const checkInTotalMinutes = inHours * 60 + inMinutes
    const checkOutTotalMinutes = outHours * 60 + outMinutes

    // Handle overnight shifts
    let totalMinutes = checkOutTotalMinutes - checkInTotalMinutes
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60 // Add 24 hours if checkout is next day
    }

    // Subtract break time
    const breakMinutes = breaks !== undefined
      ? breaks.reduce((total, brk) => {
          if (brk.endTime) {
            return total + Math.round((brk.endTime.getTime() - brk.startTime.getTime()) / 60000)
          }
          return total
        }, 0)
      : (currentData.totalBreakMinutes || 0)

    const totalWorkMinutes = Math.max(0, totalMinutes - breakMinutes)
    updates.totalWorkMinutes = totalWorkMinutes

    // Fetch user's hourly rate to calculate gross pay
    const userRef = doc(db, 'users', currentData.userId)
    const userSnap = await getDoc(userRef)
    if (userSnap.exists()) {
      const userData = userSnap.data()
      const ratePerHour = userData.ratePerHour || 0
      const grossPay = (totalWorkMinutes / 60) * ratePerHour
      updates.grossPay = Math.round(grossPay * 100) / 100 // Round to 2 decimal places
      changes.grossPay = { from: currentData.grossPay, to: updates.grossPay }
    }

    changes.totalWorkMinutes = { from: currentData.totalWorkMinutes, to: totalWorkMinutes }

    // Auto-unflag if times are now within valid range (60-720 minutes)
    // and the session was flagged due to unusual duration
    if (currentData.status === 'flagged' && currentData.flagReason?.includes('Unusually')) {
      if (totalWorkMinutes >= 60 && totalWorkMinutes <= 720) {
        updates.status = 'completed'
        updates.flagReason = null
        changes.status = { from: 'flagged', to: 'completed' }
        changes.flagReason = { from: currentData.flagReason, to: null }
      }
    }
  }

  // Update daily report if provided
  if (dailyReport !== undefined && dailyReport !== currentData.dailyReport) {
    updates.dailyReport = dailyReport
    changes.dailyReport = { from: currentData.dailyReport, to: dailyReport }
  }

  // Update flag reason (can be set to null to unflag)
  if (flagReason !== undefined && flagReason !== currentData.flagReason) {
    updates.flagReason = flagReason
    changes.flagReason = { from: currentData.flagReason, to: flagReason }
  }

  // Update breaks if provided
  if (breaks !== undefined) {
    // Convert breaks to Firestore format with Timestamps
    const updatedBreaks = breaks.map(brk => {
      const startTimestamp = Timestamp.fromDate(brk.startTime)
      const endTimestamp = brk.endTime ? Timestamp.fromDate(brk.endTime) : null

      // Calculate duration in minutes
      let duration: number | null = null
      if (brk.endTime) {
        const diffMs = brk.endTime.getTime() - brk.startTime.getTime()
        duration = Math.round(diffMs / 60000)
      }

      return {
        id: brk.id,
        startTime: startTimestamp,
        startLat: null,
        startLng: null,
        endTime: endTimestamp,
        endLat: null,
        endLng: null,
        duration
      }
    })

    updates.breaks = updatedBreaks

    // Calculate and update total break minutes
    const totalBreakMinutes = calculateTotalBreakMinutes(updatedBreaks)
    updates.totalBreakMinutes = totalBreakMinutes

    // Log change - summarize the breaks change
    const oldBreakCount = currentData.breaks?.length || 0
    const newBreakCount = updatedBreaks.length
    const oldTotalBreakMinutes = currentData.totalBreakMinutes || 0
    changes.breaks = {
      from: `${oldBreakCount} breaks (${oldTotalBreakMinutes} min)`,
      to: `${newBreakCount} breaks (${totalBreakMinutes} min)`
    }

    // If times weren't updated but breaks were, recalculate totalWorkMinutes
    if (checkInTime === undefined && checkOutTime === undefined) {
      const existingCheckInTime = currentData.checkIn?.time
      const existingCheckOutTime = currentData.checkOut?.time

      if (existingCheckInTime && existingCheckOutTime) {
        const [inH, inM] = existingCheckInTime.split(':').map(Number)
        const [outH, outM] = existingCheckOutTime.split(':').map(Number)
        const checkInMins = inH * 60 + inM
        const checkOutMins = outH * 60 + outM
        let totalMins = checkOutMins - checkInMins
        if (totalMins < 0) totalMins += 24 * 60

        const newTotalWorkMinutes = Math.max(0, totalMins - totalBreakMinutes)
        updates.totalWorkMinutes = newTotalWorkMinutes
        changes.totalWorkMinutes = { from: currentData.totalWorkMinutes, to: newTotalWorkMinutes }

        // Recalculate gross pay
        const userRef = doc(db, 'users', currentData.userId)
        const userSnap = await getDoc(userRef)
        if (userSnap.exists()) {
          const userData = userSnap.data()
          const ratePerHour = userData.ratePerHour || 0
          const newGrossPay = Math.round((newTotalWorkMinutes / 60) * ratePerHour * 100) / 100
          updates.grossPay = newGrossPay
          changes.grossPay = { from: currentData.grossPay, to: newGrossPay }
        }

        // Check if we should auto-unflag
        if (currentData.status === 'flagged' && currentData.flagReason?.includes('Unusually')) {
          if (newTotalWorkMinutes >= 60 && newTotalWorkMinutes <= 720) {
            updates.status = 'completed'
            updates.flagReason = null
            changes.status = { from: 'flagged', to: 'completed' }
            changes.flagReason = { from: currentData.flagReason, to: null }
          }
        }
      }
    }
  }

  // Only update if there are actual changes
  if (Object.keys(changes).length > 0) {
    await updateDoc(sessionRef, updates)

    // Log the edit
    await logSessionEdit(
      adminId,
      adminName,
      { id: sessionId, userName: currentData.userName },
      changes
    )
  }
}
